import { CurrencyDisplayUtil } from './../../common/currency-display-util';
import { BalanceMetricComponent } from './../balance-metric/balance-metric.component';
import { BalanceMetric } from './../balance-metric/balance-metric';
import { ThroughputMetricComponent } from './../throughput-metric/throughput-metric.component';
import { ThroughputMetric } from './../throughput-metric/throughput-metric';
import { SuspenseBalanceMetricComponent } from './../suspense-balance-metric/suspense-balance-metric.component';
import { SuspenseBalanceMetric } from './../suspense-balance-metric/suspense-balance-metric';
import { MarketDataService } from './../../market-data/market-data.service';
import { ForeignOutflowMetricComponent } from './../foreign-outflow-metric/foreign-outflow-metric.component';
import { ForeignOutflowMetric } from './../foreign-outflow-metric/foreign-outflow-metric';
import { ProjectionMetricComponent } from './../projection-metric/projection-metric.component';
import { BreachMetricComponent } from './../breach-metric/breach-metric.component';
import { ProjectionMetric } from './../projection-metric/projection-metric';
import {
    MetricTypes,
    SurvivalHorizonData,
    GetSurvivalHorizonServiceDataOutput,
    ProjectionMonitoringData,
    QuoteTenor,
    buildConventionalFXQuote,
    CurrencyUtil,
} from '@liquitics/transaction';
import { SurvivalHorizonService } from './../../survival-horizon/survival-horizon.service';
import { Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import {
    Component,
    OnInit,
    Input,
    OnDestroy,
    ChangeDetectionStrategy,
    NgZone,
    AfterViewInit,
    ChangeDetectorRef,
    ViewChildren,
    QueryList
} from '@angular/core';

import { MetricSelection } from './../metric-selection';
import { DashboardEventService } from '../../dashboard/dashboard-event.service';
import { AmChartsService } from '@amcharts/amcharts3-angular';
import { MediaChange, MediaObserver } from '@angular/flex-layout';
import { TrendMetric } from '../trend-metric/trend-metric';
import { BreachMetric } from '../breach-metric/breach-metric';
import { AppConfig } from '../../app.config';
import { MetricTile } from '../metric-tile';

const TEMP_HARD_CODED_GROSS_BREACH = 1200;
const MAX_METRICS_PER_ROW = 6;

@Component({
    selector: 'app-trend-metric',
    templateUrl: './trend-metric.component.html',
    styleUrls: ['./trend-metric.component.scss']
})
export class TrendMetricComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() public deltaBGColor: string;
    @ViewChildren(BreachMetricComponent) breachMetricComponents: QueryList<BreachMetricComponent>;
    @ViewChildren(ProjectionMetricComponent) projectionMetricComponents: QueryList<ProjectionMetricComponent>;
    @ViewChildren(ThroughputMetricComponent) throughputMetricComponents: QueryList<ThroughputMetricComponent>;
    @ViewChildren(ForeignOutflowMetricComponent) foreignOutflowMetricComponents: QueryList<ForeignOutflowMetricComponent>;
    @ViewChildren(SuspenseBalanceMetricComponent) suspenseBalanceMetricComponents: QueryList<SuspenseBalanceMetricComponent>;
    @ViewChildren(BalanceMetricComponent) balanceMetricComponents: QueryList<BalanceMetricComponent>;

    POLLING_INTERVAL = 5000;
    METRIC_ROW_WEIGHT = 4;
    METRIC_COL_WEIGHT = 4;

    private initialized = false;
    private dataStreamDisposable: Subscription;
    baseCurrency: string;
    currencies: string[] = [];
    nostroMainCurrencies: string[] = [];
    metricTypes: string[];
    private metricMap: Map<string, TrendMetric> = new Map<string, TrendMetric>();

    breachesByCcyIndex: BreachMetric[][] = [];
    startBreachesByCcyIndex: BreachMetric[][] = [];

    projectionMetricsByCcyIndex: ProjectionMetric[][] = [];

    throughputMetric: ThroughputMetric;
    foreignOutflowMetric: ForeignOutflowMetric;
    suspenseBalanceMetric: SuspenseBalanceMetric;

    actualBalanceMetrics: BalanceMetric[] = [];

    tiles: MetricTile<TrendMetric>[] = [];

    subscription: Subscription;

    constructor(private amCharts: AmChartsService,
        private survivalHorizonService: SurvivalHorizonService,
        private dashboardEventService: DashboardEventService,
        private marketDataService: MarketDataService,
        private appConfig: AppConfig,
        private media: MediaObserver) {
        if (this.appConfig.activeUserProfile.getProfileType() === 'MULTI_ENTITY_NOSTRO') {
            this.currencies = [];
        } else {
            this.currencies = this.appConfig.activeUserProfile.getCurrencies();
        }
    }

    ngOnInit() {
        this.metricTypes = this.appConfig.activeUserProfile.getMetricTypes();
        this.baseCurrency = this.appConfig.activeUserProfile.getBaseCurrency();
        this.initializeBreachesAndTiles();
        this.subscription = this.media.asObservable().subscribe((changes: MediaChange[]) => {
            this.updateMetricTiles();
            this.currencies.map((currency, ccyIndex) => {
                this.breachesByCcyIndex[ccyIndex].map((breach: BreachMetric, breachIndex: number) => {
                    if (breach.chart) {
                        this.amCharts.destroyChart(breach.chart);
                        breach.chart = null;
                    }
                });
            });
        });
    }

    private async initializeBreachesAndTiles() {
        this.metricMap.clear();
        if (this.appConfig.activeUserProfile.getProfileType() === 'MULTI_ENTITY_NOSTRO') {
            this.actualBalanceMetrics = [];
            this.nostroMainCurrencies = this.appConfig.activeUserProfile.getCurrencies().slice(0, 4);
            this.nostroMainCurrencies
                .forEach((currency: string, currencyIndex: number) => {
                    const balanceMetric = new BalanceMetric(MetricTypes.ACTUAL_BALANCE, currencyIndex);
                    balanceMetric.currency = currency;
                    this.actualBalanceMetrics.push(balanceMetric);
                    this.metricMap.set(balanceMetric.createTileKey(), balanceMetric);
                })
            this.initialized = true;
            this.updateMetricTiles();
            return Promise.resolve();
        } else {
            return Promise.all(this.currencies.map(async (currency, ccyIndex) => {
                const breaches = this.createBreaches(currency, ccyIndex);
                breaches.forEach((breach: BreachMetric) => {
                    this.metricMap.set(breach.createTileKey(), breach);
                })
                this.breachesByCcyIndex[ccyIndex] = breaches;
                try {
                    const output: GetSurvivalHorizonServiceDataOutput = await this.survivalHorizonService.getStartOfDayPositions(currency)
                    const startPositions = output.positions;
                    const startBreaches = this.createBreaches(currency, ccyIndex);
                    this.startBreachesByCcyIndex[ccyIndex] = startBreaches;
                    startBreaches.forEach((startBreach: BreachMetric) => {
                        startPositions.forEach((startPosition: SurvivalHorizonData) => {
                            startBreach.process(startPosition);
                        });
                    });
                } catch (err) {
                    console.log('Start of Day position based metrics have failed to initialize.');
                }
                const projectionMetrics = this.createProjectionMetrics(currency, ccyIndex);
                projectionMetrics.forEach((projectionMetric: ProjectionMetric) => {
                    this.metricMap.set(projectionMetric.createTileKey(), projectionMetric);
                })
                this.projectionMetricsByCcyIndex[ccyIndex] = projectionMetrics;

                // We assume only one of these exists.
                this.throughputMetric = new ThroughputMetric();
                this.throughputMetric.currency = currency;
                this.metricMap.set(this.throughputMetric.createTileKey(), this.throughputMetric);

                // We assume only one of these exists.
                this.suspenseBalanceMetric = new SuspenseBalanceMetric();
                this.suspenseBalanceMetric.currency = currency;
                this.metricMap.set(this.suspenseBalanceMetric.createTileKey(), this.suspenseBalanceMetric);

                // We assume only one of these exists.
                this.foreignOutflowMetric = new ForeignOutflowMetric();
                this.foreignOutflowMetric.currency = currency;
                this.foreignOutflowMetric.reset();
                this.metricMap.set(this.foreignOutflowMetric.createTileKey(), this.foreignOutflowMetric);
                this.initialized = true;
                this.updateMetricTiles();

            })
            );
        }
    }

    ngAfterViewInit() {
        this.dataStreamDisposable = this.survivalHorizonService.getPollingDataSubject()
            .subscribe({
                next: (data: GetSurvivalHorizonServiceDataOutput) => {
                    this.updateMetrics(data);
                }, error: (err) => {
                    console.log(err);
                    throw err;
                }
            });
        this.subscription.add(this.dashboardEventService.survivalHorizonModeSubject.subscribe({
            next: mode => {
                if (mode === 'fixed') {
                    this.survivalHorizonService
                        .getDataObservable()
                        .pipe(take(1))
                        .subscribe({
                            next: (serviceData: GetSurvivalHorizonServiceDataOutput) => {
                                this.updateMetrics(serviceData);
                            }, error: (err) => {
                                console.log(err);
                            },
                            complete: () => {
                                // Do something at end...
                            }
                        });
                }
            }
        }));
    }

    private updateMetrics(data: GetSurvivalHorizonServiceDataOutput) {
        if (!this.initialized) {
            return;
        }
        const positions = data.positions;
        if (positions == null || positions.length === 0) {
            // This should be debug eventually but fails lint atm.
            console.log('Received null or empty data. Ignoring so stale values do not get cleared...');
            return;
        }
        const projections = data.projections;
        const foreignOutflows = data.foreignOutflows;
        const throughput = data.throughput;
        const suspenseBalance = []; // data.suspenseBalance;

        this.currencies.map((currency: string, ccyIndex: number) => {
            this.breachesByCcyIndex[ccyIndex].map(breach => {
                breach.reset();
            });
            positions.filter(entry => {
                return (entry.currency.toUpperCase() === currency.toUpperCase());
            }).sort((d1, d2) => {
                if (d1.bucket === d2.bucket) {
                    return 0;
                }
                return d1.bucket < d2.bucket ? -1 : 1;
            }).forEach(entry => {
                this.breachesByCcyIndex[ccyIndex].map(breach => {
                    breach.process(entry);
                });
            });
            this.breachesByCcyIndex[ccyIndex].map((breach: BreachMetric, breachIndex: number) => {
                const startBreach = this.startBreachesByCcyIndex[ccyIndex][breachIndex];
                breach.delta = breach.daysOk - startBreach.daysOk;
            });

            const netAmounts: number = projections.filter((entry: ProjectionMonitoringData) => {
                return (entry.currency.toUpperCase() === currency.toUpperCase());
            })
                .reduce((runningAmounts: any, curr: ProjectionMonitoringData) => {
                    let netAmount = runningAmounts['net'] || 0;
                    let inAmount = runningAmounts['in'] || 0;
                    let outAmount = runningAmounts['out'] || 0;
                    netAmount += curr.excessAmount;
                    if (curr.direction === 'PAY') {
                        outAmount += curr.excessAmount;
                    } else if (curr.direction === 'REC') {
                        inAmount += curr.excessAmount;
                    }
                    runningAmounts['net'] = netAmount;
                    runningAmounts['in'] = inAmount;
                    runningAmounts['out'] = outAmount;

                    return runningAmounts;
                }, {});

            this.projectionMetricsByCcyIndex[ccyIndex].map((projectionMetric: ProjectionMetric) => {
                projectionMetric.initialized = true;
                projectionMetric.unexpectedAmount = netAmounts['net'] || 0;
                projectionMetric.unexpectedIn = netAmounts['in'] || 0;
                projectionMetric.unexpectedOut = Math.abs(netAmounts['out']) || 0;
            });

            this.foreignOutflowMetric.reset();
            foreignOutflows.forEach((outflow: SurvivalHorizonData) => {
                this.foreignOutflowMetric.currencies.push(outflow.currency);
                // TODO - Need to multiply by FX Rates here
                // const marketData = marketDataArr.find((md: MarketData) => {
                //     return md.tenor === '2D' && md.currency === outflow.currency;
                // });
                const fxRate = this.marketDataService.getSingleRate(
                    buildConventionalFXQuote(outflow.currency, this.baseCurrency, QuoteTenor.SPOT, '').name, 0.0);
                const isConventional = CurrencyUtil.isFXQuoteConvention(outflow.currency, this.baseCurrency);
                let multiplicativeRate = (isConventional ? fxRate : 1 / fxRate);
                if (fxRate === 0) {
                    multiplicativeRate = 1.0; // Temp
                }
                this.foreignOutflowMetric.netflow += multiplicativeRate * (outflow.outAmount + outflow.inAmount);
                this.foreignOutflowMetric.breakDown.push({
                    currency: outflow.currency,
                    fxRate: fxRate,
                    outAmount: outflow.outAmount,
                    inAmount: outflow.inAmount,
                    baseOutAmount: multiplicativeRate * outflow.outAmount,
                    baseInAmount: multiplicativeRate * outflow.inAmount,
                });
            })
            this.foreignOutflowMetric.netflowShort = CurrencyDisplayUtil.toShortNumber(this.foreignOutflowMetric.netflow, 2);
            this.foreignOutflowMetric.initialized = true;
            this.throughputMetric.throughput = throughput;
            this.throughputMetric.actualPayPercentage = throughput.actualPayAmount / throughput.authorizedPayAmount;
            this.throughputMetric.actualRecPercentage = throughput.actualRecAmount / throughput.authorizedRecAmount;
            this.throughputMetric.initialized = true;
            suspenseBalance.push({ balance: 1000000, payBalance: -100000, recBalance: 1100000 });
            this.suspenseBalanceMetric.balance = suspenseBalance[0]['balance'];
            this.suspenseBalanceMetric.payBalance = suspenseBalance[0]['payBalance'];
            this.suspenseBalanceMetric.recBalance = suspenseBalance[0]['recBalance'];
            this.suspenseBalanceMetric.breakDownLast10 = [];
            this.suspenseBalanceMetric.breakDownLast10.push(
                {
                    currency: 'USD', account: 'ACCOUNT1', tradeDateTime: '01-01-2018', client: 'Client1', direction: 'REC',
                    amount: 1000000, externalRef: '12345', flowKey: '12345', eventDateTime: '01-01-2018'
                });
            this.suspenseBalanceMetric.breakDownLast10.push(
                {
                    currency: 'USD', account: 'ACCOUNT1', tradeDateTime: '01-01-2018', client: 'Client1', direction: 'REC',
                    amount: 2000000, externalRef: '12345', flowKey: '12345', eventDateTime: '01-01-2018'
                });
            this.suspenseBalanceMetric.breakDownLast10.push(
                {
                    currency: 'USD', account: 'ACCOUNT1', tradeDateTime: '01-01-2018', client: 'Client1', direction: 'REC',
                    amount: 3000000, externalRef: '12345', flowKey: '12345', eventDateTime: '01-01-2018'
                });
            this.suspenseBalanceMetric.breakDownLast10.push(
                {
                    currency: 'USD', account: 'ACCOUNT1', tradeDateTime: '01-01-2018', client: 'Client1', direction: 'REC',
                    amount: 4000000, externalRef: '12345', flowKey: '12345', eventDateTime: '01-01-2018'
                });
            this.suspenseBalanceMetric.breakDownLast10.push(
                {
                    currency: 'USD', account: 'ACCOUNT1', tradeDateTime: '01-01-2018', client: 'Client1', direction: 'REC',
                    amount: 5000000, externalRef: '12345', flowKey: '12345', eventDateTime: '01-01-2018'
                });

            this.suspenseBalanceMetric.breakDownTop10 = [];
            this.suspenseBalanceMetric.breakDownTop10.push(
                {
                    currency: 'USD', account: 'ACCOUNT1', tradeDateTime: '01-01-2018', client: 'Client1', direction: 'REC',
                    amount: 1000000, externalRef: '123456', flowKey: '12345', eventDateTime: '01-01-2018'
                });
            this.suspenseBalanceMetric.breakDownTop10.push(
                {
                    currency: 'USD', account: 'ACCOUNT1', tradeDateTime: '01-01-2018', client: 'Client1', direction: 'REC',
                    amount: 2000000, externalRef: '123456', flowKey: '12345', eventDateTime: '01-01-2018'
                });
            this.suspenseBalanceMetric.breakDownTop10.push(
                {
                    currency: 'USD', account: 'ACCOUNT1', tradeDateTime: '01-01-2018', client: 'Client1', direction: 'REC',
                    amount: 3000000, externalRef: '123456', flowKey: '12345', eventDateTime: '01-01-2018'
                });
            this.suspenseBalanceMetric.breakDownTop10.push(
                {
                    currency: 'USD', account: 'ACCOUNT1', tradeDateTime: '01-01-2018', client: 'Client1', direction: 'REC',
                    amount: 4000000, externalRef: '123456', flowKey: '12345', eventDateTime: '01-01-2018'
                });
            this.suspenseBalanceMetric.breakDownTop10.push(
                {
                    currency: 'USD', account: 'ACCOUNT1', tradeDateTime: '01-01-2018', client: 'Client1', direction: 'REC',
                    amount: 5000000, externalRef: '123456', flowKey: '12345', eventDateTime: '01-01-2018'
                });

            this.suspenseBalanceMetric.initialized = true;
        }
        );
        this.currencies.map((currency, ccyIndex) => {
            const breaches = this.breachesByCcyIndex[ccyIndex];
            breaches.forEach((breach: BreachMetric, breachIndex: number) => {
                setTimeout(() => {
                    this.refreshChart(breach, ccyIndex, breachIndex);
                    this.breachMetricComponents.forEach((bmc: BreachMetricComponent) => {
                        bmc.refreshMetric();
                    });
                }, 8);
            });
            setTimeout(() => {
                this.projectionMetricComponents.forEach((pmc: ProjectionMetricComponent) => {
                    pmc.refreshMetric();
                });
                this.throughputMetricComponents.forEach((tmc: ThroughputMetricComponent) => {
                    tmc.refreshMetric();
                });
                this.foreignOutflowMetricComponents.forEach((fomc: ForeignOutflowMetricComponent) => {
                    fomc.refreshMetric();
                });
                this.suspenseBalanceMetricComponents.forEach((smc: SuspenseBalanceMetricComponent) => {
                    smc.refreshMetric();
                });
            }, 8);
        });
        setTimeout(() => {
            this.actualBalanceMetrics.forEach((actualMetric: BalanceMetric, actualIndex: number) => {
                const component: BalanceMetricComponent = this.balanceMetricComponents.toArray()[actualIndex];
                component.balanceMetric = actualMetric;
                const foundActualBalance = data.actualBalances.find((actualBalance: any) => {
                    return actualBalance.currency === actualMetric.currency;
                })
                if (foundActualBalance) {
                    actualMetric.balance = foundActualBalance.amount;
                }
                actualMetric.initialized = true;
                component.refreshMetric();
            })
        }, 8);
    }

    private refreshChart(breach: BreachMetric, ccyIndex: number, metricIndex: number) {
        const dataProvider = [];
        breach.chartData.map((d: any, dataIndex: number) => {
            return d;
        })
            .reduce((chunks: any[], next) => {
                let chunk: any[] = chunks.pop();
                if (!chunk) {
                    chunk = [];
                } else if (chunk.length >= 10) {
                    chunks.push(chunk);
                    chunk = [];
                }
                chunks.push(chunk);
                chunk.push(next);
                return chunks;
            }, [])
            .forEach((chunk: any[]) => {
                const summed = chunk.reduce((prev, next) => {
                    prev.value += next.value;
                    if (next.bullet) {
                        prev.bullet = next.bullet;
                    }
                    return prev;
                });
                summed.value = summed.value / 10;
                dataProvider.push(summed);
            });

        if (!breach.chart) {
            breach.chartDivName = 'chart' + ccyIndex + '-' + metricIndex;
            setTimeout(() => {
                breach.chart = breach.createChart(this.amCharts, ccyIndex, metricIndex, dataProvider);
                breach.chart.graphs[0].lineColor = breach.colors[1];
                this.refreshChart(breach, ccyIndex, metricIndex);
            }, (100 * (breach.currencyIndex + 1))); // Cascade drawing so we don't get cpu spikes
        } else {
            this.amCharts.updateChart(breach.chart, () => {
                breach.chart.dataProvider = dataProvider;
                breach.chart.graphs[0].lineColor = breach.colors[1];
                // breach.chart.graphs[0].fillColors = [breach.colors[1]];
                breach.chart.validateData();
                breach.chart.invalidateSize();
            });
        }
        // this.breaches[index].chartData.forEach((d: any, dataIndex: number) => {
        //     if (dataIndex % 10 === 0) {
        //         chart.dataProvider.push(d);
        //     }
        // });

    }

    private refreshCharts() {
        this.currencies.map((currency: string, ccyIndex: number) => {
            this.breachesByCcyIndex[ccyIndex].map((breach: BreachMetric, breachIndex: number) => {
                this.refreshChart(breach, ccyIndex, breachIndex);
            });
        });
    }

    createBreaches(currency: string, ccyIndex: number): BreachMetric[] {
        const arr: BreachMetric[] = [];
        const survivalBreach = new BreachMetric(MetricTypes.SURVIVAL_HORIZON, currency.toUpperCase() + ' Survival', currency, ccyIndex,
            (entry) => {
                return entry.liquiditySurplus;
            },
            (entry) => {
                return entry.liquiditySurplus >= 0;
            },
            (breach, entry) => {
                breach.submetric1 = '1.30';
                breach.submetric2 = '1.20';
                breach.submetric3 = '1.15';
            }
        );
        const netBreach = new BreachMetric(MetricTypes.NET_BREACH, currency.toUpperCase() + ' Net Breach', currency, ccyIndex,
            (entry) => {
                return entry.netAmount;
            },
            (entry) => {
                return entry.netAmount >= 0;
            },
            (breach, entry) => {
                breach.submetric1 = '1.30';
                breach.submetric2 = '1.20';
                breach.submetric3 = '1.15';
            }
        );
        const grossBreach = new BreachMetric(MetricTypes.GROSS_BREACH, currency.toUpperCase() + ' Gross Breach', currency, ccyIndex,
            (entry) => {
                return (entry.inAmount + entry.outAmount);
            },
            (entry) => {
                return (entry.inAmount + entry.outAmount) <= TEMP_HARD_CODED_GROSS_BREACH;
            },
            (breach, entry) => {
                breach.submetric1 = '1.30';
                breach.submetric2 = '1.20';
                breach.submetric3 = '1.15';
            }
        );

        arr.push(survivalBreach);
        arr.push(netBreach);
        arr.push(grossBreach);

        return arr;
    }

    createProjectionMetrics(currency: string, ccyIndex: number): ProjectionMetric[] {
        const projectionMetrics: ProjectionMetric[] = [];
        const projectionMetric = new ProjectionMetric();
        projectionMetric.currency = currency;
        projectionMetric.currencyIndex = ccyIndex;
        projectionMetric.unexpectedAmount = 0;
        projectionMetric.unexpectedIn = 0;
        projectionMetric.unexpectedOut = 0;
        projectionMetrics.push(projectionMetric);
        return projectionMetrics;
    }

    ngOnDestroy() {
        this.currencies.map((currency: string, ccyIndex: number) => {
            this.breachesByCcyIndex[ccyIndex].map((breach: BreachMetric, breachIndex: number) => {
                if (breach.chart) {
                    this.amCharts.destroyChart(breach.chart);
                    breach.chart = null;
                }
            });
        });
        if (this.dataStreamDisposable) {
            this.dataStreamDisposable.unsubscribe();
        }
        this.subscription.unsubscribe();
    }

    private columnCount() {
        if (this.media.isActive('lt-md')) {
            return 2 + (3 * this.currencies.length);
        } else {
            const rowHeaderCount = (this.currencies.length === 0) ? 0 : 1;
            if (this.metricTypes.length > MAX_METRICS_PER_ROW) {
                return ((this.metricTypes.length / 2) * this.METRIC_ROW_WEIGHT) + (rowHeaderCount);
            } else {
                return (this.metricTypes.length * this.METRIC_ROW_WEIGHT) + (rowHeaderCount);
            }
        }
    }

    trackTile(index, tile) {
        return index;
    }

    private updateMetricTiles() {
        this.tiles = [];
        const mobile = (this.media.isActive('lt-md'));

        if (mobile) {
            // Empty corner cell
            this.tiles.push({
                type: 'none',
                cols: 2,
                rows: 2
            } as MetricTile<TrendMetric>);
            // Currency Headers
            this.currencies.map((currency: string, ccyIndex: number) => {
                // Currency Row Header
                this.tiles.push({
                    type: 'currency',
                    value: currency.toUpperCase(),
                    cols: 3,
                    rows: 2
                } as MetricTile<TrendMetric>
                );
            });
            // One row per metric type
            this.metricTypes.map((metricType: string, metricIndex: number) => {
                this.tiles.push({
                    type: 'metricType',
                    value: metricType,
                    cols: 2,
                    rows: this.METRIC_ROW_WEIGHT
                } as MetricTile<TrendMetric>);
                this.currencies.forEach((currency: string, ccyIndex: number) => {
                    const tileKey = TrendMetric.toTileKey(currency, metricType);
                    const trendMetric: TrendMetric = this.metricMap.get(tileKey);
                    const tileType = (trendMetric) ? trendMetric.tileType : '';
                    this.tiles.push({
                        type: tileType,
                        metric: trendMetric,
                        cols: 3,
                        rows: this.METRIC_ROW_WEIGHT
                    } as MetricTile<TrendMetric>
                    );
                });
            });
        } else {
            const currencyHeadersAdded = new Set<string>();
            const metricTypesArray: any[] = [];
            const workingMetricTypes: any[] = this.metricTypes.slice();
            let metricsPerRow = workingMetricTypes.length;
            if (workingMetricTypes.length > MAX_METRICS_PER_ROW) {
                metricsPerRow = metricsPerRow / 2;
            }
            while (workingMetricTypes.length > 0) {
                metricTypesArray.push(workingMetricTypes.splice(0, metricsPerRow));
            }
            metricTypesArray.forEach((stripOfMetricTypes: string[]) => {
                // Empty corner cell
                if (this.currencies.length > 0) {
                    this.tiles.push({
                        type: 'none',
                        cols: 1,
                        rows: 1
                    } as MetricTile<TrendMetric>);
                }
                // Metric headers
                this.tiles.push(...stripOfMetricTypes.map((metricType: string) => {
                    return {
                        type: 'metricType',
                        value: metricType,
                        cols: this.METRIC_COL_WEIGHT,
                        rows: 1
                    } as MetricTile<TrendMetric>;
                }));
                // Now Currency Breach rows
                this.currencies.map((currency: string, ccyIndex: number) => {
                    // Currency Row Header
                    let currencyHeaderValue = currency.toUpperCase();
                    if (currencyHeadersAdded.has(currency)) {
                        currencyHeaderValue = '';
                    }
                    currencyHeadersAdded.add(currency);
                    this.tiles.push({
                        type: 'currency',
                        value: currencyHeaderValue,
                        cols: 1,
                        rows: this.METRIC_ROW_WEIGHT
                    } as MetricTile<TrendMetric>
                    );
                    stripOfMetricTypes.map((metricType: string, metricIndex: number) => {
                        const tileKey = TrendMetric.toTileKey(currency, metricType);
                        const trendMetric: TrendMetric = this.metricMap.get(tileKey);
                        const tileType = (trendMetric) ? trendMetric.tileType : '';
                        this.tiles.push({
                            type: tileType,
                            metric: trendMetric,
                            cols: this.METRIC_COL_WEIGHT,
                            rows: this.METRIC_ROW_WEIGHT
                        } as MetricTile<TrendMetric>
                        );
                    })
                });
                // Refactor this later
                if (this.currencies.length === 0) {
                    stripOfMetricTypes.map((metricType: string, metricIndex: number) => {
                        const tileKey = TrendMetric.toTileKey(String(metricIndex), metricType);
                        const trendMetric: TrendMetric = this.metricMap.get(tileKey);
                        const tileType = (trendMetric) ? trendMetric.tileType : '';
                        this.tiles.push({
                            type: tileType,
                            metric: trendMetric,
                            cols: this.METRIC_COL_WEIGHT,
                            rows: this.METRIC_ROW_WEIGHT
                        } as MetricTile<TrendMetric>
                        );
                    })
                }
            });
        }
    }
}


